// US-090: pure helpers for AddProductsDialog.vue, extracted so they unit-test without
// mounting the component (same split as add_to_po_drawer_helpers.ts).

/**
 * Drop the supplier products that are already on the purchase order.
 *
 * Callers pass variant ids rather than PO line items: the Edit-PO page holds
 * IPurchaseOrderItem[] and the Create-PO page holds table rows, and the only thing the
 * dialog needs from either is which variants are already spoken for.
 */
export function excludeAlreadyOnPo<T extends { variant_id: number | string }>(
  products: T[],
  excludeVariantIds: Array<number | string>,
): T[] {
  const excluded = new Set<number>((excludeVariantIds || []).map((id) => Number(id)));
  return products.filter((p) => !excluded.has(Number(p.variant_id)));
}

/**
 * US-090: decides when AddProductsDialog actually has to hit the network.
 *
 * The endpoint behind the dialog (GET /supplier-products/supplier/:id) has no cheap
 * path — it scans the supplier's whole SupplierProduct set and runs one OLAP query over
 * every variant id before searching, sorting and paginating in memory — so every
 * avoided call is a full catalog fetch avoided. Two calls were pure waste:
 *
 *  - reopening the dialog for the same supplier refetched everything, because the
 *    dialog-open watcher reset all state unconditionally;
 *  - searching from any page but the first fired twice, because fetchProducts(true)
 *    mutates tableOptions.page, which wakes the deep watcher that also fetches.
 *
 * Kept as a plain class rather than component state so both rules are unit-testable
 * without mounting the dialog.
 */
export class AddProductsFetchGate {
  private loadedSupplierId: number | null = null;
  private suppressNextOptionsChange = false;

  /**
   * The dialog opened. True when results have to be fetched — nothing loaded yet, or
   * loaded for a different supplier.
   */
  public onOpen(supplierId: number | null): boolean {
    if (supplierId == null) { return false; }
    if (this.loadedSupplierId === supplierId) { return false; }
    this.loadedSupplierId = supplierId;
    return true;
  }

  /** Call immediately before mutating tableOptions in code rather than from the UI. */
  public suppressNextWatch(): void {
    this.suppressNextOptionsChange = true;
  }

  /** The tableOptions deep watcher fired. True when it should actually fetch. */
  public onOptionsChanged(): boolean {
    if (this.suppressNextOptionsChange) {
      this.suppressNextOptionsChange = false;
      return false;
    }
    return true;
  }

  /** Drop the cached load so the next open refetches (explicit refresh). */
  public invalidate(): void {
    this.loadedSupplierId = null;
  }
}
